import { db } from '../../db/indexeddb'
import {
  getCloudTags,
  getCloudTagIds,
  getCloudTagsByIds,
  getDeletedCloudTags,
  createCloudTag,
  updateCloudTag,
  deleteCloudTag,
} from '../../supabase/queries'
import { CloudTagSchema, CloudTagPayloadSchema } from '../sync-schemas'
import type { PullDescriptor } from '../sync-pull'
import type { PushDescriptor } from '../sync-push'
import type { Tag } from '../../db/models'

/* ── Tag ─────────────────────────────────────────── */

export const tagPullDescriptor: PullDescriptor<any> = {
  entityName: 'tag',
  settingsKey: 'lastTagSyncTime',

  fetchRemote: (userId, since) => getCloudTags(userId, since),
  fetchRemoteIds: (userId) => getCloudTagIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudTagsByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudTags(userId, since),

  safeParse: (raw) => CloudTagSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.tags,
  getAllLocal: () => db.tags.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.tags.filter(t => !t.deletedAt).count()
    return count === 0
  },

  buildLocalInsert: async (remote) => ({
    id: crypto.randomUUID(),
    name: remote.name,
    color: remote.color,
    description: remote.description ?? null,
    usageCount: remote.usage_count,
    userId: remote.user_id,
    cloudId: remote.id,
    deletedAt: null,
    importedFrom: remote.imported_from ?? null,
    createdAt: new Date(remote.created_at),
    updatedAt: new Date(remote.updated_at),
    lastModified: new Date(remote.updated_at),
    synced: true,
    syncedAt: new Date(),
  }),

  buildLocalUpdate: async (remote) => ({
    name: remote.name,
    color: remote.color,
    description: remote.description ?? null,
    usageCount: remote.usage_count,
    ...('imported_from' in remote ? { importedFrom: remote.imported_from ?? null } : {}),
    updatedAt: new Date(remote.updated_at),
  }),
}

export const tagPushDescriptor: PushDescriptor<Tag> = {
  entityName: 'CloudTag',
  getLocal: (id) => db.getTag(id),
  getLocalCloudId: (t) => t.cloudId,
  getLocalId: (t) => t.id,
  table: db.tags,

  buildCreatePayload: async (tag) => CloudTagPayloadSchema.parse({
    name: tag.name,
    color: tag.color,
    description: tag.description,
    usage_count: tag.usageCount,
    local_id: tag.id,
    imported_from: tag.importedFrom ?? null,
  }),

  buildUpdatePayload: async (tag) => {
    const payload: Record<string, any> = CloudTagPayloadSchema.parse({
      name: tag.name,
      color: tag.color,
      description: tag.description,
      usage_count: tag.usageCount,
      local_id: tag.id,
      imported_from: tag.importedFrom ?? null,
    })
    if (!tag.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudTag(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudTag(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudTag(cloudId, userId),
}
